import { GenericAbortSignal } from 'axios';

import { GroupType, GroupTypeOptions } from 'models/flexible/GroupType';
import { Paginated } from 'models/flexible/Paginated';
import type {
  AdminGroupTypeCreateParams,
  AdminGroupTypeIndexFields,
  AdminGroupTypeIndexInclude,
  AdminGroupTypeUpdateParams,
  GroupTypeFields,
} from 'services/api/fields';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const groupTypesQueryKeys = {
  index: (params?: Record<string, any>) => ['groupTypes', params] as const,
  show: (id: string) => ['groupTypes', id] as const,
};

const URL = 'admin/group_types';

export type GroupTypeIndexParams = BaseIndexQueryParams & {
  clientId?: string;
  type?: string;
  inpatient?: string;
  apiName?: string;
  'apiName.not'?: string;
};

export async function indexGroupTypes<I extends AdminGroupTypeIndexInclude = never>(
  params: GroupTypeIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<GroupType<AdminGroupTypeIndexFields<I>>>> {
  return http
    .get<
      Paginated<GroupTypeOptions>
    >(URL, { params: { ...params, ...(includes?.length ? { include: includes.join(',') } : {}) }, signal })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((x) => new GroupType(x)),
    })) as Promise<Paginated<GroupType<AdminGroupTypeIndexFields<I>>>>;
}

export async function showGroupType(id: string, signal?: GenericAbortSignal): Promise<GroupType<GroupTypeFields>> {
  return http.get<GroupTypeOptions>(`${URL}/${id}`, { signal }).then((res) => new GroupType(res.data)) as Promise<
    GroupType<GroupTypeFields>
  >;
}

export async function createGroupType(params: AdminGroupTypeCreateParams): Promise<GroupType<GroupTypeFields>> {
  return http.post<GroupTypeOptions>(URL, params).then((res) => new GroupType(res.data)) as Promise<
    GroupType<GroupTypeFields>
  >;
}

export async function updateGroupType(params: AdminGroupTypeUpdateParams): Promise<GroupType<GroupTypeFields>> {
  const { id, ...body } = params;
  return http.patch<GroupTypeOptions>(`${URL}/${id}`, body).then((res) => new GroupType(res.data)) as Promise<
    GroupType<GroupTypeFields>
  >;
}
